import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quotes } from "@phosphor-icons/react";
import { api, mediaUrl } from "../lib/api";
import useSEO from "../lib/useSEO";
import KoshMark from "../components/KoshMark";

/**
 * /case-studies/:slug — SEO detail page for one deployment.
 *
 * Renders the full problem/solution/outcome narrative, an oversized
 * testimonial pull-quote when present, tech-stack pills, and a strong CTA
 * to open KOSH or /contact. Every case study gets its own indexable URL +
 * schema.org JSON-LD (CaseStudy → Article schema).
 */
export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCs(null); setNotFound(false);
    api.get(`/case-studies/${slug}`)
      .then(({ data }) => { if (!cancelled) setCs(data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/case-studies/${slug}` : `https://wisewit.ai/case-studies/${slug}`;
  const description = (cs?.seo_description || cs?.outcome || cs?.problem || "").slice(0, 160);

  useSEO({
    title: cs ? `${cs.title} — Wisewit case study` : "Case study — Wisewit",
    description,
    canonical,
    ogImage: cs?.cover_image ? mediaUrl(cs.cover_image) : undefined,
    ogType: "article",
    schema: cs ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${canonical}#article`,
          "headline": cs.title,
          "description": description,
          "articleSection": cs.industry,
          "url": canonical,
          "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
          "image": cs.cover_image ? {
            "@type": "ImageObject",
            "url": mediaUrl(cs.cover_image),
          } : undefined,
          "datePublished": cs.created_at || undefined,
          "dateModified": cs.updated_at || cs.created_at || undefined,
          "author": {
            "@type": "Organization",
            "name": "Wisewit",
            "url": "https://wisewit.ai",
          },
          "publisher": {
            "@type": "Organization",
            "name": "Wisewit",
            "url": "https://wisewit.ai",
            "logo": {
              "@type": "ImageObject",
              "url": "https://wisewit.ai/logo.png",
            },
          },
          "keywords": (cs.tech || []).join(", "),
          "about": cs.kosh_involvement ? "KOSH industrial LLM copilot deployment" : undefined,
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": canonical.replace(/\/case-studies\/.+$/, "/") },
            { "@type": "ListItem", "position": 2, "name": "Case studies", "item": canonical.replace(/\/[^/]+$/, "") },
            { "@type": "ListItem", "position": 3, "name": cs.title, "item": canonical },
          ],
        },
        cs.testimonial_quote ? {
          "@type": "Review",
          "reviewBody": cs.testimonial_quote,
          "author": { "@type": "Person", "name": cs.testimonial_author || "Wisewit customer" },
          "itemReviewed": {
            "@type": "SoftwareApplication",
            "name": cs.kosh_involvement ? "KOSH" : "Wisewit",
            "applicationCategory": "BusinessApplication",
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
          },
        } : undefined,
      ].filter(Boolean),
    } : undefined,
  });

  if (notFound) {
    return (
      <div className="px-6 md:px-14 py-24 max-w-3xl">
        <div className="text-overline mb-2">◢ Not found</div>
        <h1 className="font-display text-3xl text-white">This case study doesn't exist.</h1>
        <p className="mt-3 text-[#8E8E93]">It may have been unpublished or the URL is wrong.</p>
        <Link to="/#deployments" className="btn-secondary mt-6 inline-flex"><ArrowLeft size={12} /> All deployments</Link>
      </div>
    );
  }
  if (!cs) {
    return <div className="px-6 md:px-14 py-24 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">Loading case study…</div>;
  }

  return (
    <div data-testid="case-study-detail" className="px-6 md:px-14 py-16 md:py-24">
      {/* Breadcrumb */}
      <Link to="/#deployments" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] hover:text-[#FF4500] inline-flex items-center gap-2 mb-6">
        <ArrowLeft size={12} /> Deployments
      </Link>

      <div className="grid md:grid-cols-[1.5fr_1fr] gap-10 max-w-6xl">
        <div>
          <div className="text-overline">◢ {cs.industry}</div>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-3 font-display font-bold tracking-tight text-white text-4xl md:text-5xl leading-[0.95]"
          >
            {cs.title}
          </motion.h1>

          {cs.kosh_involvement && (
            <div
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF4500]/50 bg-[#FF4500]/[0.06]"
              data-testid="cs-kosh-badge"
            >
              <KoshMark size={12} tone="on-glass" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500]">
                KOSH-featured deployment
              </span>
            </div>
          )}

          {/* Testimonial pull-quote */}
          {cs.testimonial_quote && (
            <blockquote
              className="mt-10 border-l-2 border-[#DFFF00] pl-6 py-2"
              data-testid="cs-testimonial"
            >
              <Quotes size={22} color="#DFFF00" weight="fill" className="mb-3" />
              <p className="font-display text-2xl md:text-[28px] text-white leading-snug">
                {cs.testimonial_quote}
              </p>
              {cs.testimonial_author && (
                <footer className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">
                  — {cs.testimonial_author}
                </footer>
              )}
            </blockquote>
          )}

          <div className="mt-10 space-y-8">
            <Block label="Problem" body={cs.problem} accent="#FF3060" />
            <Block label="Solution" body={cs.solution} accent="#DFFF00" />
            <Block label="Outcome" body={cs.outcome} accent="#00FFB2" />
          </div>

          {cs.impact_metrics?.length > 0 && (
            <div className="mt-12">
              <div className="text-overline mb-4">◢ Impact metrics</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
                {cs.impact_metrics.map((m, i) => (
                  <div key={i} className="bg-[#050505] p-5">
                    <div className="font-display text-3xl text-[#FF4500]">{m.value}</div>
                    <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">Scope a similar deployment <ArrowRight size={14} /></Link>
            {cs.kosh_involvement && (
              <button onClick={() => window.dispatchEvent(new CustomEvent("kosh:open", { detail: { seed: `Tell me more about how KOSH was used at ${cs.title}` } }))} className="btn-secondary">
                <KoshMark size={14} tone="on-glass" /> Ask KOSH about this
              </button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {cs.cover_image && (
            <div className="border border-[#2A2A2A]">
              <img src={mediaUrl(cs.cover_image)} alt={cs.title} className="w-full h-auto object-cover" loading="lazy" />
            </div>
          )}
          {cs.tech?.length > 0 && (
            <div className="ww-card p-5">
              <div className="text-overline mb-3">◢ Tech</div>
              <div className="flex flex-wrap gap-1.5">
                {cs.tech.map((t) => (
                  <span key={t} className="font-mono text-[10px] tracking-[0.14em] px-2 py-1 border border-[#2A2A2A] text-[#D7D7D9]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Block({ label, body, accent }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase" style={{ color: accent }}>◢ {label}</div>
      <p className="mt-2 text-[#D7D7D9] text-[15px] leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}
